import { inject, Injectable } from "@angular/core";
import { select, Store } from "@ngrx/store";
import { Observable } from "rxjs";
import { filter, first, map, take, tap, withLatestFrom } from "rxjs/operators";

import {
	CustomerInfoDTO,
	GuestInfoPayloadDTO,
	PageEntryPointModel,
	VoucherDTO,
} from "@guest-web/api";
import {
	ClearGuestInfoEntryPoint,
	CreateGuest,
	DeleteGuest,
	RetrieveGuestInfo,
	RetrieveGuestsList,
	RetrieveLoyaltyProgram,
	RetrieveVouchersList,
	selectCustomerProfileLoading,
	selectCustomerProfileUpdateSuccess,
	selectGuestCreateSuccess,
	selectGuestDeleteSuccess,
	selectGuestInfoEntryPoint,
	selectGuestsList,
	selectGuestUpdateSuccess,
	selectLoyaltyProgram,
	selectVouchersList,
	SetGuestInfoEntryPoint,
	SetSelectedGuest,
	UpdateCustomer,
	UpdateCustomerProfile,
	UpdateGuest,
} from "@guest-web/core";
import { TrpToastOptions, TrpToastUtil } from "@triparound/trp-components/feedback";
import { ignoreNil, TrpNotificationType, TrpPosition } from "@triparound/trp-components/shared";

import {
	LoyaltyProgramPayload,
	LoyaltyProgramResponse,
} from "../../../../@api/src/lib/customer-profile/models/loyalty-program.model";
import { CommonFacadeService } from "../@core/services/common-facade.service";
import { CustomerFacadeService } from "../@core/services/customer-facade.service";
import { NavigationFacadeService } from "../@core/services/navigation-facade.service";
import { AuthenticationFacadeService } from "../authentication/authentication-facade.service";

@Injectable()
export class CustomerProfileFacadeService {
	store = inject(Store);
	toastUtil = inject(TrpToastUtil);
	authenticationFacadeService = inject(AuthenticationFacadeService);
	customerFacadeService = inject(CustomerFacadeService);
	navigationFacade = inject(NavigationFacadeService);
	commonFacade = inject(CommonFacadeService);

	updateCustomerInfoSuccess$: Observable<boolean> = this.store.select(
		selectCustomerProfileUpdateSuccess,
	);
	createGuestSuccess$: Observable<boolean> = this.store.select(selectGuestCreateSuccess);
	updateGuestSuccess$: Observable<boolean> = this.store.select(selectGuestUpdateSuccess);
	deleteGuestSuccess$: Observable<boolean> = this.store.select(selectGuestDeleteSuccess);

	isLoading$: Observable<boolean> = this.store.select(selectCustomerProfileLoading);
	guestsList$: Observable<CustomerInfoDTO[]> = this.store.select(selectGuestsList);
	vouchersList$: Observable<VoucherDTO[]> = this.store.select(selectVouchersList);
	guestInfoEntryPoint$: Observable<PageEntryPointModel> = this.store.pipe(
		select(selectGuestInfoEntryPoint),
	);

	loyaltyProgram$: Observable<LoyaltyProgramResponse> = this.store
		.select(selectLoyaltyProgram)
		.pipe(
			ignoreNil(),
			tap((loyaltyProgram: LoyaltyProgramResponse) =>
				this.store.dispatch(
					new UpdateCustomer({
						acctkn: loyaltyProgram.acctkn || null,
						acid: loyaltyProgram.acid || null,
						profileEuros: loyaltyProgram.profileEuros || null,
					}),
				),
			),
		);

	retrieveGuestsList(): void {
		this.store.dispatch(
			new RetrieveGuestsList({ customerUuid: this.customerFacadeService.customerInfo.uuid }),
		);
	}

	retrieveLoyaltyProgram(payload: LoyaltyProgramPayload): void {
		this.store.dispatch(new RetrieveLoyaltyProgram(payload));
	}

	getSelectedGuest(guestId: number): Observable<CustomerInfoDTO | null> {
		return this.store
			.select(selectGuestsList)
			.pipe(map(guests => guests.find(guest => guest.id === guestId)));
	}

	saveCustomerData(customerData: any, customerUuid: string, message: string): void {
		this.store.dispatch(new UpdateCustomerProfile({ customerData, customerUuid }));

		this.updateCustomerInfoSuccess$
			.pipe(
				filter(success => success),
				take(1),
			)
			.subscribe(() => {
				const data: TrpToastOptions = {
					title: "",
					message,
					notificationPosition: TrpPosition.Right,
					asNotification: true,
					type: TrpNotificationType.Success,
				};

				this.authenticationFacadeService.updateUserData({
					...this.customerFacadeService.customerInfo,
					...customerData,
				});
				this.store.dispatch(new UpdateCustomer(customerData));
				this.toastUtil.showToastAsNotification(data);
				this.navigationFacade.go({
					path: [`${this.commonFacade.baseUrl}/profile/personal-info`],
				});
			});
	}

	createGuest(guestData: GuestInfoPayloadDTO): void {
		this.store.dispatch(
			new CreateGuest({
				guestData,
				customerUuid: this.customerFacadeService.customerInfo.uuid,
			}),
		);
		this.createGuestSuccess$
			.pipe(
				filter(success => success),
				take(1),
			)
			.subscribe(() => {
				const message = "Guest added successfully";
				const data: TrpToastOptions = {
					title: "",
					message,
					notificationPosition: TrpPosition.Right,
					asNotification: true,
					type: TrpNotificationType.Success,
				};
				console.log(data);

				this.navigationFacade.go({
					path: [`${this.commonFacade.baseUrl}/profile/your-guests`],
				});
				this.toastUtil.showToastAsNotification(data);
			});
	}

	updateGuest(guestData: GuestInfoPayloadDTO, successMessage: string, guestId: string): void {
		this.store.dispatch(
			new UpdateGuest({
				guestData,
				guestId,
				customerUuid: this.customerFacadeService.customerInfo.uuid,
			}),
		);
		this.updateGuestSuccess$
			.pipe(
				filter(success => success),
				take(1),
			)
			.subscribe(() => {
				const data: TrpToastOptions = {
					title: "",
					message: successMessage,
					notificationPosition: TrpPosition.Right,
					asNotification: true,
					type: TrpNotificationType.Success,
				};

				this.navigationFacade.go({
					path: [`${this.commonFacade.baseUrl}/profile/your-guests/${guestId}`],
				});
				this.toastUtil.showToastAsNotification(data);
			});
	}

	deleteGuest(guestUuid: string): void {
		this.store.dispatch(
			new DeleteGuest({
				guestUuid,
				customerUuid: this.customerFacadeService.customerInfo.uuid,
			}),
		);
		this.deleteGuestSuccess$
			.pipe(
				filter(success => success),
				withLatestFrom(this.guestInfoEntryPoint$),
				take(1),
			)
			.subscribe(([, entryPoint]) => {
				const data: TrpToastOptions = {
					title: "",
					message: "Guest deleted successfully!",
					notificationPosition: TrpPosition.Right,
					asNotification: true,
					type: TrpNotificationType.Success,
				};

				const path = entryPoint?.path ?? `${this.commonFacade.baseUrl}/profile/your-guests`;
				this.navigationFacade.go({ path: [path] });
				this.toastUtil.showToastAsNotification(data);
			});
	}

	getActiveVouchers(): void {
		this.store.dispatch(
			new RetrieveVouchersList({
				customerUuid: this.customerFacadeService.customerInfo.uuid,
				active: true,
			}),
		);
	}

	getInActiveVouchers(): void {
		this.store.dispatch(
			new RetrieveVouchersList({
				customerUuid: this.customerFacadeService.customerInfo.uuid,
				active: false,
			}),
		);
	}

	setSelectedGuest(guest: CustomerInfoDTO): void {
		this.store.dispatch(new SetSelectedGuest({ ...guest }));
	}

	retrieveGuestInfo(id: number): void {
		this.store.dispatch(
			new RetrieveGuestInfo({
				id,
				customerUuid: this.customerFacadeService.customerInfo.uuid,
			}),
		);
	}

	checkGuestInfoExists(guestId: number): void {
		this.getSelectedGuest(guestId)
			.pipe(
				tap(customer => {
					if (!customer || customer.id !== guestId) {
						this.retrieveGuestInfo(guestId);
						return null;
					}
				}),
				first(),
			)
			.subscribe();
	}

	setGuestInfoEntryPoint(payload: PageEntryPointModel): void {
		this.store.dispatch(new SetGuestInfoEntryPoint(payload));
	}

	clearGuestInfoEntryPoint(): void {
		this.store.dispatch(new ClearGuestInfoEntryPoint());
	}
}
